/*
Map screen — primary view. Hosts the MapLibre canvas, the FAB stack
(remarquables / arboretum / profile), the cold-start splash, a
transient toast for capture feedback, and the bottom-sheet detail
panel that opens on tap of a tree pin.

Capture model (sheet-driven, mirrors Android):
- Tap a tree pin → bottom sheet opens with the arbre's identity (species
  name if revealed; "Inconnu" otherwise), live distance, per-arbre
  details if captured (height / circ / address / id), and a "Capturer"
  button gated by proximity (≤ kMaxArbreMeters).
- Tapping "Capturer" runs runCaptureFlow against THAT arbre. The
  handler is synchronous through to capturePhoto() so the iOS user-
  gesture context is preserved.
*/

// Type-only import keeps MapView (and its `maplibre-gl` dependency, ~600 KB
// gzipped) out of the static graph. The runtime import lives inside
// `mountMapScreen` so MapLibre is only fetched + parsed when the user
// actually lands on the map. Profile / Arboretum / Species / Badges /
// Welcome cold-load drops by the size of the MapLibre chunk.
import type { MapController, ArbreTapPayload } from '../map/MapView'
import { attachMap, isMapCreated } from '../map/mapHost'
import { navigate, kPaths, type Route } from '../router'
import { runCaptureFlow, kMaxArbreMeters, kHysteresisMeters, type CaptureStatus, type TargetArbre } from '../capture/captureFlow'
import { triggerCaptureTapHaptic, triggerCaptureSuccessFlash, triggerCaptureCancelTic } from '../capture/feedback'
import { ensureSpeciesIndex, speciesNameFor, displayName, allSpecies, isDiscovered, effectivelyCapturedSpecies, genreFilterSet, type SpeciesName } from '../data/speciesIndex'
import { capturedArbreIds, capturedSpeciesIndices, listCaptures, listCapturesByArbre, deleteCapture } from '../data/captureRepository'
import { ensureGenreInfo, genreInfoFor } from '../data/genreInfo'
import { ensureArrSpecies } from '../data/arrSpecies'
import { ensureArbresFeatures, applyDiscoveryToFeatures, discoveryFCForSpecies } from '../data/arbresFeatures'
import { ensureArbreAttrs, attrsFor, type ArbreAttrs } from '../data/arbreAttrs'
import { ensureSpeciesInfo, speciesInfoFor, medianComparison } from '../data/speciesInfo'
import { arrKeyLabel } from '../data/arrKey'
import { mountHuntPanel, type HuntPanelController } from './components/HuntPanel'
import { getRadarObscured, setRadarObscured } from '../data/radarObscureStore'
import { remarquableInfoFor, ensureRemarquablesInfo } from '../data/remarquablesInfo'
import { getLatestFix, getRawFix, requestFreshFix, subscribeFix, subscribeDenied, isGeolocationDenied, haversineMeters, kMaxGpsAgeMs, type FreshFix } from '../capture/geolocation'
import { loadPhoto } from '../data/photoStore'
import { renderSplashHtml, renderCaptureSplashHtml, startSplash, kFilterSplashMinMs } from './components/SplashOverlay'
import { openLightbox, type LightboxPhoto } from './components/PhotoLightbox'
import { mountPhotoGallery, type PhotoGalleryController, type PhotoGalleryItem } from './components/PhotoGallery'
import { confirmDeleteCapture } from './components/DeleteCaptureDialog'
import { tryPersistStorage } from '../data/persistentStorage'
import type { Capture } from '../data/types'

// Fly-to-exact-arbre constants — Android v1.0.2 *Photos & progressivité*:
// "fly-to ~600 ms à zoom 20 (très fort zoom — un seul pin à l'écran)
//  et un pulse blanc 2 s sur la position".
const kPulseZoom: number = 20  // close enough that there's no ambiguity about which pin we're highlighting
const kPulseFlyDurationMs: number = 600
const kPulseDurationMs: number = 2000

// Pending banner state stashed across the species-celebrate detour: the
// first capture immediately navigates to the species detail screen, so we
// can't render the banner inline — we set a localStorage flag and read it
// the next time the map is mounted.
const kPersistBannerKey: string = 'canopaname.banner.persistStorage'  // localStorage key
type PersistBannerState = 'granted' | 'denied'  // values written under kPersistBannerKey

let fActiveMap: MapController | null = null  // the persistent MapLibre controller (lives in mapHost), reassigned each mount
let fDiscoveryDirty: boolean = false  // a capture/delete/import changed discovery since the last enrich → re-enrich (under the conditional splash) on the next map mount; with a persistent map the enriched source survives, so we skip the costly 213k-FC setData otherwise

/*
L'import de backup (ProfileScreen) appelle ceci après ≥1 capture importée :
le prochain mount de la carte relance l'enrich species-wide sous son splash
conditionnel. Export nommé, pas de cycle (MapScreen n'importe pas
ProfileScreen).
*/
export function markDiscoveryDirty(): void
{
	fDiscoveryDirty = true
}
// Which dataset the single persistent MapLibre source currently holds:
// 'full' (every Paris tree) or a set of species (filtered map mode, Android
// `map_filtered/{speciesIndices}`). Survives across mounts because the
// source itself does — used on the next mount to decide whether the full
// set must be restored (returning from a filtered view) or can be left as-is.
let fSourceMode: 'full' | { sks: number[] } = 'full'

// v1.5.0 — filtre rapide in-place depuis la sheet d'un pin (parité Android
// MapHost.quickFilter). Distinct du mode filtré par route : appliqué sans
// navigation, caméra immobile (« le cadrage de l'utilisateur est sacré »),
// défiltrage ✕ in-place. Persiste au remount comme la source qu'il pilote
// (le remount réaliste sous filtre est le détour fiche/capture — perdre le
// filtre au retour serait une régression) ; le subset est recomputé au
// re-apply, donc self-heal après capture/suppression/import.
type QuickFilter =
	| { kind: 'species'; sk: number; label: string }
	| { kind: 'genre'; genre: string; label: string }
let fQuickFilter: QuickFilter | null = null
let fRouteFiltered: boolean = false  // mount courant en mode filtré par route ? (masque les boutons quick — parité Android : quickFilter en carte normale seulement)
let fCaptureInFlight: boolean = false  // simple lock — disallow concurrent captures
let fSheetTarget: ArbreTapPayload | null = null  // currently-open arbre, full payload incl. lat/lon/hm/cm/addr/arr
let fCapturedArbres: Set<number> = new Set()  // arbreIds captured (gates "Fiche remarquable" + per-arbre details)
let fCapturedSpecies: Set<number> = new Set()  // speciesIndex captured (gates "Fiche espèce" + post-capture reveal of the species name)
let fSheetFixUnsub: (() => void) | null = null  // active geolocation subscription, cancelled on sheet close
let fMountGen: number = 0  // bumped on every mount; lets the async MapLibre import bail out if the user already navigated away
let fSheetGallery: PhotoGalleryController | null = null  // mounted gallery component for the currently-open sheet, destroyed on close/refresh
let fSheetGalleryUrls: string[] = []  // object URLs created for the current gallery render, revoked on next render or close
let fSheetGalleryLoadGen: number = 0  // bumped per gallery render so a late blob load can detect being superseded

// Mode chasse Étoile (HuntPanel). État volatile (parité Android
// `remember`) : se réinitialise au unmount/remount de l'écran carte.
let fHuntActive: boolean = false  // panel ouvert ?
let fHuntController: HuntPanelController | null = null  // ref pour destroy + refresh

// GPS FAB pulse visual — short halo on tap so the user sees the press
// registered even before the fly-to lands. Matches the CSS @keyframes
// pulse-gps duration.
const kGpsPulseDurationMs: number = 1_500

// Miroir de la snackbar Android « Permission de localisation refusée »
// (MapScreen.kt), suffixé du chemin de sortie — sur iOS un refus ne
// re-déclenche jamais l'invite, il faut repasser par les Réglages.
const kGeoDeniedToast = 'Permission de localisation refusée — autorise la Position dans les Réglages.'
let fDeniedUnsub: (() => void) | null = null  // abonnement refus géoloc du mount courant
let fDeniedToastShown = false  // toast refus montré au plus une fois par session (hors tap FAB explicite)

/*
Mount the map screen. Builds the DOM, lazily fetches the MapLibre
chunk, mounts the map, then wires the FAB stack + arbre-tap → sheet →
capture pipeline.

Returns a Promise so the dynamic import of `../map/MapView` can be
awaited, but the router treats it as void — the splash overlay covers
the chunk-load gap on cold start, and `fMountGen` aborts the async
work if the user navigated to a different screen meanwhile.
*/
export async function mountMapScreen(inRoot: HTMLElement, inRoute?: Route): Promise<void>
{
	const vGen = ++fMountGen  // capture this mount's generation; compare after every await
	const vPulseArbreId = (inRoute && inRoute.id === 'map' && typeof inRoute.pulseArbreId === 'number')
		? inRoute.pulseArbreId
		: null
	// Filtered map mode (Android `map_filtered/{speciesIndices}`) — only the
	// set's trees, fit-to-bbox, "Réveil des …" splash + a back banner. A
	// singleton set comes from the fiche-espèce, a larger one from the fiche
	// genre (`{sk (G, sp.)} ∪ {sks capturés du genre}`). Names resolve
	// synchronously when arriving from a fiche (which loads the index);
	// falls back gracefully on a cold deep-link.
	const vFilterSpecies: number[] | null =
		(inRoute && inRoute.id === 'map' && Array.isArray(inRoute.filterSpecies) && inRoute.filterSpecies.length > 0)
			? inRoute.filterSpecies
			: null
	// Representative entry (parity Android MapScreen): the set's (G, sp.)
	// entry when present (genre filter), else the first resolvable one.
	let vFilterName: SpeciesName | null = null
	if (vFilterSpecies !== null)
	{
		const vNames = vFilterSpecies.map((inSk) => speciesNameFor(inSk))
		vFilterName = vNames.find((inN) => inN?.unknownSpecies === true) ?? vNames.find((inN) => inN !== null) ?? null
	}
	// Splash label: a (G, sp.) entry reads better as the genre's French name
	// (« Prunier ») than as « Prunier (Prunus sp.) » — parity Android.
	const vFilterLabel = vFilterName
		? (vFilterName.unknownSpecies
			? (genreInfoFor(vFilterName.genre)?.fr ?? truncateBeforeParen(displayName(vFilterName)))
			: displayName(vFilterName))
		: null
	// Genre-filter subtitle on the banner (parity Android `genreSubtitle`):
	// captured-in-set over the genre's identified entries — denominator NOT
	// isActive-filtered, like Android entriesOfGenre.count { !unknownSpecies }.
	let vGenreSubtitle: string | null = null
	if (vFilterSpecies !== null && vFilterSpecies.length > 1 && vFilterName)
	{
		const vGenre = vFilterName.genre
		const vTotalGenre = allSpecies().filter((inE) => inE.name.genre === vGenre && !inE.name.unknownSpecies).length
		const vCapturedInSet = vFilterSpecies.filter((inSk) => speciesNameFor(inSk)?.unknownSpecies === false).length
		vGenreSubtitle = vTotalGenre > 0 ? `${vCapturedInSet} / ${vTotalGenre} espèces du genre` : null
	}

	// The MapLibre instance is PERSISTENT (see map/mapHost) — never destroyed
	// on navigation, only detached/re-attached across the router's innerHTML
	// wipe. `vColdMapMount` is true only on the very first creation: show the
	// splash + await pins then; warm returns skip it (the map is already
	// rendered, re-attach is instant and allocates nothing).
	const vColdMapMount = !isMapCreated()
	// Travail de re-coloration nécessaire en mode plein, décidable
	// synchroniquement au render (fSourceMode / fDiscoveryDirty sont
	// module-level) : cold start, capture/import dirty, ou retour d'une
	// carte filtrée (la source ne contient plus que le sous-ensemble).
	// Gouverne le splash conditionnel warm — la carte serait sinon montrée
	// grise/filtrée pendant l'enrich (setData 213k + recluster).
	// Un mount en mode route filtrée supplante un quick filter résiduel
	// (les deux pilotent la même source unique — le plus récent gagne).
	if (vFilterSpecies !== null) fQuickFilter = null
	fRouteFiltered = vFilterSpecies !== null
	const vNeedsFullRefresh = vFilterSpecies === null && fQuickFilter === null
		&& (vColdMapMount || fDiscoveryDirty || fSourceMode !== 'full')
	if (fSheetFixUnsub) { fSheetFixUnsub(); fSheetFixUnsub = null }
	if (fDeniedUnsub) { fDeniedUnsub(); fDeniedUnsub = null }
	// HuntPanel reset au remount : libère rAF + DOM même si l'écran est
	// re-mount sans toucher au FAB ★ (eg. router fly-to deep-link).
	if (fHuntController) { fHuntController.destroy(); fHuntController = null }
	fHuntActive = false

	inRoot.innerHTML = `
		<div class="screen${vFilterSpecies !== null || fQuickFilter !== null ? ' map-filtered' : ''}">
			<button class="fab fab-frosted fab-search" id="fab-search" title="Rechercher" aria-label="Rechercher">
				<svg class="fab-icon" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M15.5 14h-.79l-.28-.27C15.41 12.59 16 11.11 16 9.5 16 5.91 13.09 3 9.5 3S3 5.91 3 9.5 5.91 16 9.5 16c1.61 0 3.09-.59 4.23-1.57l.27.28v.79l5 4.99L20.49 19l-4.99-5zm-6 0C7.01 14 5 11.99 5 9.5S7.01 5 9.5 5 14 7.01 14 9.5 11.99 14 9.5 14z"/></svg>
			</button>
			<button class="fab fab-frosted fab-locate" id="fab-gps" title="Me localiser" aria-label="Me localiser">
				<svg class="fab-icon" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 8c-2.21 0-4 1.79-4 4s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm8.94 3A8.994 8.994 0 0 0 13 3.06V1h-2v2.06A8.994 8.994 0 0 0 3.06 11H1v2h2.06A8.994 8.994 0 0 0 11 20.94V23h2v-2.06A8.994 8.994 0 0 0 20.94 13H23v-2h-2.06zM12 19c-3.87 0-7-3.13-7-7s3.13-7 7-7 7 3.13 7 7-3.13 7-7 7z"/></svg>
			</button>
			<button class="fab-star" id="fab-hunt" title="Mode chasse" aria-label="Mode chasse">
				<svg class="fab-icon" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 17.27L18.18 21l-1.64-7.03L22 9.24l-7.19-.61L12 2 9.19 8.63 2 9.24l5.46 4.73L5.82 21z"/></svg>
			</button>
			<div class="fab-stack">
				<button class="fab" id="fab-remarquables" title="Remarquables" aria-label="Remarquables">
					<svg class="fab-icon" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true"><circle cx="12" cy="12" r="10.5" fill="#FB8C00"/><path fill="#F5F1E6" d="M10.99 17.92 L10.99 13.87 C9.30 13.87 7.95 13.19 7.27 11.84 C6.60 11.50 6.60 10.15 6.93 9.13 C6.60 7.79 7.61 6.44 8.96 6.44 C9.30 5.42 10.65 4.75 12 4.75 C13.35 4.75 14.70 5.42 15.04 6.44 C16.39 6.44 17.40 7.79 17.07 9.13 C17.40 10.15 17.40 11.50 16.73 11.84 C16.05 13.19 14.70 13.87 13.01 13.87 L13.01 17.92 Z"/></svg>
				</button>
				<button class="fab" id="fab-arboretum" title="Arboretum" aria-label="Arboretum">
					<svg class="fab-icon fab-icon-arboretum" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M21 5c-1.11-.35-2.33-.5-3.5-.5-1.95 0-4.05.4-5.5 1.5-1.45-1.1-3.55-1.5-5.5-1.5S2.45 4.9 1 6v14.65c0 .25.25.5.5.5.1 0 .15-.05.25-.05C3.1 20.45 5.05 20 6.5 20c1.95 0 4.05.4 5.5 1.5 1.35-.85 3.8-1.5 5.5-1.5 1.65 0 3.35.3 4.75 1.05.1.05.15.05.25.05.25 0 .5-.25.5-.5V6c-.6-.45-1.25-.75-2-1zm-1 12.5c-1.1-.35-2.3-.5-3.5-.5-1.7 0-4.15.65-5.5 1.5V8c1.35-.85 3.8-1.5 5.5-1.5 1.2 0 2.4.15 3.5.5v11.5zM17.5 10.5c1.27 0 2.55.16 3.5.43v-2.06c-.96-.23-2.22-.36-3.5-.36-1.31 0-2.61.13-3.78.31v2.13c1.1-.27 2.45-.45 3.78-.45zM17.5 13.16c1.27 0 2.55.16 3.5.43v-2.06c-.96-.23-2.22-.36-3.5-.36-1.31 0-2.61.13-3.78.31v2.13c1.1-.28 2.45-.45 3.78-.45zM17.5 15.83c1.27 0 2.55.16 3.5.43v-2.06c-.96-.23-2.22-.36-3.5-.36-1.31 0-2.61.13-3.78.31v2.13c1.1-.29 2.45-.45 3.78-.45z"/></svg>
				</button>
				<button class="fab" id="fab-profile" title="Profil" aria-label="Profil">
					<svg class="fab-icon" width="24" height="24" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M12 5.9c1.16 0 2.1.94 2.1 2.1s-.94 2.1-2.1 2.1S9.9 9.16 9.9 8s.94-2.1 2.1-2.1m0 9c2.97 0 6.1 1.46 6.1 2.1v1.1H5.9V17c0-.64 3.13-2.1 6.1-2.1M12 4C9.79 4 8 5.79 8 8s1.79 4 4 4 4-1.79 4-4-1.79-4-4-4zm0 9c-2.67 0-8 1.34-8 4v3h16v-3c0-2.66-5.33-4-8-4z"/></svg>
				</button>
			</div>
			<div class="toast" id="toast" data-open="false" role="status" aria-live="polite"></div>
			<div id="hunt-panel-host"></div>
			${vFilterSpecies !== null ? renderFilterBanner(vFilterName, vGenreSubtitle) : ''}
			${renderQuickFilterBanner()}
			${renderPersistBanner()}
			${renderArbreSheet()}
			${vFilterSpecies !== null
				? renderSplashHtml(vFilterLabel ?? 'arbres')
				: vColdMapMount
					? renderSplashHtml()
					: vNeedsFullRefresh ? renderSplashHtml('arbres') : ''}
		</div>
	`
	// Promise resolver pattern: startSplash needs a way to wait for the
	// real map pins to render, but the MapController is built later (after
	// the lazy MapLibre chunk lands). We pass the splash a callback that
	// awaits the controller, then delegates to its awaitArbresRendered.
	// If the chunk fails or the user navigates away, the resolver is
	// never called — the splash's kColdSplashMaxMs safety catches that.
	//
	// `resolveMap` is assigned synchronously (Promise executors run inline),
	// so it's set before any later call. Holder object (not a bare `let`):
	// TS narrows a `let x = null` to `null` and ignores the closure
	// reassignment below, breaking the later call.
	//
	// Splash shows on the cold first creation, on every filtered mount
	// (the source swap + fitBounds takes a beat — the "Réveil des …" splash
	// covers it, parity Android FilterSplash), AND on a warm return to the
	// full map that owes a re-coloration (back from filtered, capture/import
	// dirty) — the spinner variant covers the enrich setData instead of
	// showing a stale/grey map. A clean warm return re-attaches an
	// already-rendered map — nothing to wait for, no splash.
	const vSplash: { resolveMap: ((inMap: MapController) => void) | null } = { resolveMap: null }
	if (vColdMapMount || vFilterSpecies !== null || vNeedsFullRefresh)
	{
		const vMapReady = new Promise<MapController>((inResolve) => { vSplash.resolveMap = inResolve })
		const vAwaitPins = async (): Promise<boolean> => {
			const vMap = await vMapReady
			const vRendered = await vMap.awaitArbresRendered(15_000)
			// Map is fully ready here (style loaded, sources hydrated, at
			// least one cluster/point painted). No geolocation to trigger
			// here — the watchPosition started in main.ts feeds the
			// custom user-location layer directly via subscribeFix.
			console.log(`[mapscreen] awaitArbresRendered=${vRendered}`)
			return vRendered
		}
		if (vFilterSpecies !== null || !vColdMapMount)
		{
			// Filtered mount OR warm full-refresh: short floor, no tips.
			void startSplash(inRoot, vAwaitPins, { floorMs: kFilterSplashMinMs, skipTips: true })
		}
		else
		{
			void startSplash(inRoot, vAwaitPins)
		}
	}

	maybeShowPersistBanner(inRoot)

	inRoot.querySelector('#fab-arboretum')?.addEventListener('click', () => navigate(kPaths.arboretum()))
	inRoot.querySelector('#fab-profile')?.addEventListener('click', () => navigate(kPaths.profile()))
	inRoot.querySelector('#fab-remarquables')?.addEventListener('click', () => navigate(kPaths.remarquables()))
	inRoot.querySelector('#fab-gps')?.addEventListener('click', () => onGpsFabTap(inRoot))
	inRoot.querySelector('#fab-hunt')?.addEventListener('click', () => onHuntFabTap(inRoot))
	inRoot.querySelector('#fab-search')?.addEventListener('click', () => onSearchFabTap(inRoot))
	inRoot.querySelector('#sheet-backdrop')?.addEventListener('click', () => closeSheet(inRoot))
	inRoot.querySelector('#sheet-close')?.addEventListener('click', () => closeSheet(inRoot))
	inRoot.querySelector('#sheet-species')?.addEventListener('click', () => onSheetSpecies())
	inRoot.querySelector('#sheet-remarquable')?.addEventListener('click', () => onSheetRemarquable())
	inRoot.querySelector('#sheet-capture')?.addEventListener('click', () => onSheetCapture(inRoot))
	inRoot.querySelector('#sheet-filter-species')?.addEventListener('click', () => onSheetFilterSpecies(inRoot))
	inRoot.querySelector('#sheet-filter-genre')?.addEventListener('click', () => onSheetFilterGenre(inRoot))
	inRoot.querySelector('#quick-filter-clear')?.addEventListener('click', () => clearQuickFilter(inRoot))
	inRoot.querySelector('#persist-banner-close')?.addEventListener('click', () => dismissPersistBanner(inRoot))
	// Filtered-map back banner → return to the fiche-espèce (parity Android
	// popBackStack). history.back() lands on the previous entry (the fiche);
	// a cold deep-link with no history falls back to the full map.
	inRoot.querySelector('#filter-banner-back')?.addEventListener('click', () => {
		if (history.length > 1) history.back()
		else navigate(kPaths.map())
	})

	// Refus de permission géoloc → toast (miroir snackbar Android). Couvre
	// le refus au boot/« Commencer », sinon muet. Une fois par session —
	// le tap FAB GPS garde sa propre remontée à chaque tentative.
	fDeniedUnsub = subscribeDenied(() => {
		if (fDeniedToastShown) return
		fDeniedToastShown = true
		showToast(inRoot, kGeoDeniedToast, 'error', 6000)
	})

	void ensureSpeciesIndex().catch((inErr: unknown) => {
		console.warn('Species index preload failed', inErr)
	})

	// Attach the persistent map into this freshly-built screen. On the cold
	// mount this lazy-imports MapLibre (~600 KB) and creates the instance; on
	// warm returns it just re-parents the existing node + resize() — instant,
	// zero allocation, no geojson re-parse (the whole point of mapHost).
	const vScreen = inRoot.querySelector('.screen') as HTMLElement | null
	if (!vScreen) return
	try
	{
		fActiveMap = await attachMap(vScreen)
	}
	catch (vErr)
	{
		console.error('MapLibre chunk failed to load', vErr)
		return
	}
	if (vGen !== fMountGen) return  // user navigated away during the chunk load — bail before wiring
	fActiveMap.onPointTap((inPayload) => openSheetFor(inRoot, inPayload))
	// The splash's resolveMap is deliberately NOT called here: every path
	// resolves it inside the async block below, AFTER its setSourceData when
	// one is owed (filtered swap, full-map enrich). awaitArbresRendered waits
	// on isSourceLoaded, which setData re-flips to false — so the splash
	// dismisses on the NEW pins, never on a flash of the stale source.

	void (async () => {
		try
		{
			const [vArbreSet, vSpeciesSet] = await Promise.all([capturedArbreIds(), capturedSpeciesIndices()])
			if (vGen !== fMountGen) return  // late resolve after a navigation away
			fCapturedArbres = vArbreSet
			fCapturedSpecies = vSpeciesSet
			// Feedback instantané : vert sur les arbres capturés individuellement
			// (feature-state, sans rebuild de cluster). L'enrichment / la FC filtrée
			// ci-dessous étend ensuite le vert à toute l'espèce + recolore les clusters.
			fActiveMap?.applyInitialDiscovered(fCapturedArbres)  // feature-state, cheap — reapply every mount (setData wipes it; persistent map keeps it otherwise)

			if (vFilterSpecies !== null)
			{
				// Mode filtré : on bascule l'UNIQUE source persistante sur le set
				// d'espèces (pas de 2ᵉ instance MapLibre → la fuite de la mini-carte
				// disparaît par construction). setData re-clusterise sur le sous-
				// ensemble (compteurs corrects), fitBounds cadre sur son emprise.
				await ensureArbresFeatures()
				if (vGen !== fMountGen) return
				const vEffective = effectivelyCapturedSpecies(fCapturedSpecies)
				const vFiltered = discoveryFCForSpecies(new Set(vFilterSpecies), vEffective, fCapturedArbres)
				if (vFiltered)
				{
					fActiveMap?.setSourceData(vFiltered.collection)
					fActiveMap?.fitBounds(vFiltered.bbox)
					const vCount = vFiltered.collection.features.length
					setText(inRoot, '#filter-banner-count', `${vCount} arbre${vCount > 1 ? 's' : ''} dans Paris`)
				}
				fSourceMode = { sks: vFilterSpecies }
				// Now that the filtered source is in flight, let the splash's
				// awaitArbresRendered start — it'll resolve on the filtered pins.
				if (vSplash.resolveMap && fActiveMap) vSplash.resolveMap(fActiveMap)
				return
			}

			if (fQuickFilter !== null)
			{
				// v1.5.0 : un quick filter a survécu au remount (détour fiche/
				// capture) — re-push du subset recomputé depuis les captures
				// courantes (self-heal), caméra immobile, banner ré-affiché.
				// fDiscoveryDirty éventuel reste posé : la restauration pleine
				// tournera au clear ou au prochain mount sans filtre.
				await pushQuickSubset(inRoot)
				if (vGen !== fMountGen) return
				showQuickBanner(inRoot)
				if (vSplash.resolveMap && fActiveMap) vSplash.resolveMap(fActiveMap)
				return
			}

			// Mode plein. L'enrichment complet (setData → discovered_count espèce-
			// wide + clusters, parité Android) est coûteux (FC 213k + reindex
			// clusters) ; la source enrichie survit aux navigations grâce à la carte
			// persistante, donc on ne le relance QUE si nécessaire :
			//   - cold mount : 1ʳᵉ création, rien n'est encore enrichi ;
			//   - fDiscoveryDirty : une capture/suppression/import a changé la
			//     découverte ;
			//   - retour d'un mode filtré : la source ne contient plus qu'une espèce,
			//     il FAUT restaurer le jeu complet.
			// Sinon (retour simple), on SKIP — c'est ce qui évitait le pic mémoire.
			// L'enrich tourne EAGER sous le splash (cold complet ou spinner warm) ;
			// fDiscoveryDirty/fSourceMode ne sont clearés QUE sur push réussi, pour
			// qu'une navigation pendant l'enrich laisse le prochain mount retenter.
			const vWasFiltered = fSourceMode !== 'full'
			const vHasCaptures = fCapturedSpecies.size > 0 || fCapturedArbres.size > 0
			const vNeedsEnrich = ((vColdMapMount || fDiscoveryDirty) && vHasCaptures) || vWasFiltered
			if (!vNeedsEnrich)
			{
				fDiscoveryDirty = false  // dirty sans capture (tout supprimé) : rien à peindre
				if (vSplash.resolveMap && fActiveMap) vSplash.resolveMap(fActiveMap)
				return
			}
			try
			{
				if (vColdMapMount)
				{
					// Cold : séquencer l'enrich APRÈS le rendu initial MapLibre du
					// même geojson — évite la concurrence des deux parses 32 Mo sur
					// le main thread (raison historique du défèrement idle). Timeout
					// 12 s < safety splash 15 s pour que l'enrich parte quand même
					// avant la dismissal forcée. Le splash reste ouvert.
					await fActiveMap?.awaitArbresRendered(12_000)
					if (vGen !== fMountGen) return
				}
				const vPushed = await enrichAndPushDiscovery(vGen)
				if (vPushed)
				{
					fDiscoveryDirty = false
					fSourceMode = 'full'
				}
			}
			finally
			{
				// Enrich OK, échoué ou bail : le splash doit tomber (le floor court
				// fait office de transition ; sinon safety 15 s). Sur échec, dirty
				// reste true → retente au prochain mount.
				if (vGen === fMountGen && vSplash.resolveMap && fActiveMap) vSplash.resolveMap(fActiveMap)
			}
		}
		catch (inErr)
		{
			console.warn('Initial discovery state failed', inErr)
		}
	})()

	if (vPulseArbreId !== null) void runFlyToPulse(vGen, vPulseArbreId)
}

/*
Bottom-sheet markup. Rendered once at mount; visibility driven by data-open.
Inner content (#sheet-title / #sheet-details / #sheet-capture text…) is
rewritten on each tap.
*/
function renderArbreSheet(): string
{
	return `
		<div class="sheet" id="arbre-sheet" data-open="false" role="dialog" aria-labelledby="sheet-title">
			<div class="sheet-backdrop" id="sheet-backdrop"></div>
			<div class="sheet-panel">
				<button class="sheet-close" id="sheet-close" aria-label="Fermer">×</button>
				<div class="sheet-remarquable-badge" id="sheet-remarquable-badge" hidden>
					<svg class="sheet-remarquable-badge-icon" viewBox="0 0 24 24" aria-hidden="true">
						<circle cx="12" cy="12" r="10.5" fill="#FB8C00"/>
						<path fill="#F5F1E6" d="M10.99 17.92 L10.99 13.87 C8.96 13.87 7.34 13.05 6.52 11.45 C5.48 10.65 5.48 8.61 6.04 7.39 C5.39 5.92 6.56 4.31 7.84 4.10 C8.40 2.85 10.07 2.07 12 2.07 C13.93 2.07 15.60 2.85 16.16 4.10 C17.44 4.31 18.61 5.92 17.96 7.39 C18.52 8.61 18.52 10.65 17.48 11.45 C16.66 13.05 15.04 13.87 13.01 13.87 L13.01 17.92 Z"/>
					</svg>
					<span class="sheet-remarquable-badge-label">Arbre remarquable</span>
				</div>
				<h2 class="display sheet-title" id="sheet-title">Arbre</h2>
				<div class="sheet-subtitle" id="sheet-subtitle"></div>
				<div class="sheet-details" id="sheet-details" data-visible="false"></div>
				<div class="sheet-photos" id="sheet-photos" data-visible="false"></div>
				<div class="sheet-actions">
					<button class="sheet-button sheet-button-tonal" id="sheet-remarquable" data-remarquable="false">Fiche remarquable</button>
					<button class="sheet-button sheet-button-outlined" id="sheet-species" data-visible="true">Fiche espèce</button>
					<div class="sheet-filter-row" id="sheet-filter-row" data-visible="false">
						<button class="sheet-button sheet-button-outlined sheet-button-filter" id="sheet-filter-species">
							<svg class="sheet-filter-icon" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M4.25 5.61C6.27 8.2 10 13 10 13v6c0 .55.45 1 1 1h2c.55 0 1-.45 1-1v-6s3.72-4.8 5.74-7.39C20.25 4.95 19.78 4 18.95 4H5.04c-.83 0-1.3.95-.79 1.61z"/></svg>
							Toute l'espèce
						</button>
						<button class="sheet-button sheet-button-outlined sheet-button-filter" id="sheet-filter-genre" data-visible="false">
							<svg class="sheet-filter-icon" width="18" height="18" viewBox="0 0 24 24" aria-hidden="true"><path fill="currentColor" d="M4.25 5.61C6.27 8.2 10 13 10 13v6c0 .55.45 1 1 1h2c.55 0 1-.45 1-1v-6s3.72-4.8 5.74-7.39C20.25 4.95 19.78 4 18.95 4H5.04c-.83 0-1.3.95-.79 1.61z"/></svg>
							Tout le genre
						</button>
					</div>
					<button class="sheet-button sheet-button-primary" id="sheet-capture">Capturer</button>
				</div>
			</div>
		</div>
	`
}

/*
Open the bottom sheet for a freshly-tapped arbre.
- Discovery rule on the title (species name only revealed after first capture of that species).
- Per-arbre details revealed only once captured (contrat de découverte Android).
- Live distance subscription drives the capture button state.
*/
function openSheetFor(inRoot: HTMLElement, inPayload: ArbreTapPayload): void
{
	fSheetTarget = inPayload
	// LongPress-style haptic on sheet open — Android v1.0.1 spec.
	// Soft fail on iOS Safari where vibrate() is a no-op.
	if (typeof navigator !== 'undefined' && typeof navigator.vibrate === 'function')
	{
		try { navigator.vibrate(20) } catch { /* sandboxed PWA edge case */ }
	}
	// Spotlight the tapped arbre with the pulsing ring layer for as
	// long as the sheet is open. Cleared in closeSheet.
	fActiveMap?.setActiveArbre({ lat: inPayload.lat, lon: inPayload.lon })

	// `vSpeciesDiscovered` : espèce débloquée (capture d'un membre, +
	// auto-déblocage genre-based des (G, sp.)) — parité Android
	// `SpeciesIndex.isDiscovered`. `vIsDiscovered` ajoute la règle remarquable :
	// un remarquable reste "inconnu" tant qu'il n'est pas capturé
	// individuellement, même si son espèce est par ailleurs débloquée
	// (parité MapScreen.kt:898).
	const vSpeciesDiscovered = isDiscovered(inPayload.speciesIndex, fCapturedSpecies)
	const vIsDiscovered = inPayload.remarquable
		? fCapturedArbres.has(inPayload.id)
		: vSpeciesDiscovered
	const vArbreCaptured = fCapturedArbres.has(inPayload.id)  // capture individuelle stricte — gate galerie photo + boutons remarquable
	const vName = speciesNameFor(inPayload.speciesIndex)  // {genre, espece, commonName} or null

	// Cascade nv → nc → latin (parity Android v1.1.0 displayNomCommun).
	// The sheet's subtitle hides itself when the display name already IS
	// the binomial (avoids "Acer platanoides / Acer platanoides" redite).
	const vDisplay = vIsDiscovered && vName ? displayName(vName) : ''
	const vLatin = vIsDiscovered && vName ? formatLatin(vName) : ''
	const vTitle = vIsDiscovered ? (vDisplay || 'Arbre inconnu') : 'Arbre inconnu'
	// Libellés inconnus — parité ArbreDetailScreen.kt UnknownContent.
	const vSubtitle = vIsDiscovered
		? (vLatin && vLatin !== vDisplay ? vLatin : '')
		: (inPayload.remarquable
			? 'Remarquable non découvert. Capture-le pour révéler sa fiche.'
			: 'Non capturé. Capture un arbre de cette espèce et tous les semblables se déverrouilleront.')
	setText(inRoot, '#sheet-title', vTitle)
	setText(inRoot, '#sheet-subtitle', vSubtitle)
	const vBadge = inRoot.querySelector<HTMLElement>('#sheet-remarquable-badge')
	if (vBadge) vBadge.hidden = !inPayload.remarquable

	// Per-arbre details — fetched lazily from the companion details file
	// as soon as the species is discovered (capture d'un membre suffit, parité
	// Android : la fiche révèle hauteur/circ de CET arbre précis). The fetch is
	// the first hit's only cost; subsequent sheet opens read from the in-memory
	// map synchronously.
	const vDetails = inRoot.querySelector<HTMLElement>('#sheet-details')
	if (vDetails)
	{
		if (vIsDiscovered)
		{
			vDetails.dataset.visible = 'true'
			const vCachedAttrs = attrsFor(inPayload.id)  // sync read; null until first ensureArbreAttrs resolves
			vDetails.innerHTML = vCachedAttrs
				? renderDetailsHtml(inPayload.id, vCachedAttrs, inPayload.speciesIndex)
				: '<div class="sheet-detail-row"><span>Chargement…</span></div>'
			// Re-render quand les deux sidecars (attrs + species-info pour les
			// médianes) sont en mémoire — no-op réseau s'ils sont déjà chargés.
			void Promise.all([ensureArbreAttrs(), ensureSpeciesInfo()]).then(() => {
				if (fSheetTarget?.id !== inPayload.id) return  // user moved on; bail
				const vEl = inRoot.querySelector<HTMLElement>('#sheet-details')
				if (!vEl) return
				const vAttrs = attrsFor(inPayload.id)
				vEl.innerHTML = vAttrs ? renderDetailsHtml(inPayload.id, vAttrs, inPayload.speciesIndex) : `<div class="sheet-detail-row sheet-detail-id"><span>ID OpenData : ${inPayload.id}</span></div>`
			}).catch((inErr: unknown) => { console.warn('Sheet details fetch failed', inErr) })
		}
		else
		{
			vDetails.innerHTML = ''
			vDetails.dataset.visible = 'false'
		}
	}

	// Buttons that depend purely on capture state (no live updates needed).
	const vSpeciesBtn = inRoot.querySelector<HTMLElement>('#sheet-species')
	if (vSpeciesBtn) vSpeciesBtn.setAttribute('data-visible', vSpeciesDiscovered ? 'true' : 'false')
	const vRemarquableBtn = inRoot.querySelector<HTMLElement>('#sheet-remarquable')
	if (vRemarquableBtn) vRemarquableBtn.setAttribute('data-remarquable', (inPayload.remarquable && vArbreCaptured) ? 'true' : 'false')

	// v1.5.0 filtres rapides — carte normale seulement (pas le mode route
	// filtrée, parité Android `host != null`), pin non remarquable, espèce
	// résolue, pin découvert (parité Android quickFilterEntry,
	// MapScreen.kt). Un quick filter déjà actif n'interdit PAS de
	// re-filtrer depuis la sheet (rétrécir genre → espèce ou élargir
	// espèce → genre) : applyQuickFilter remplace le filtre courant,
	// comme Android.
	const vCanQuick = !fRouteFiltered
		&& !inPayload.remarquable && vName !== null && vSpeciesDiscovered
	inRoot.querySelector<HTMLElement>('#sheet-filter-row')?.setAttribute('data-visible', vCanQuick ? 'true' : 'false')
	// « Tout le genre » masqué en plus quand le set genre est vide ou réduit
	// au singleton de l'espèce ouverte (bouton redondant) — parité Android
	// onFilterGenre (genreFilterSks != setOf(entry.index)).
	let vGenreQuick = false
	if (vCanQuick && vName)
	{
		const vGenreSet = genreFilterSet(vName.genre, fCapturedSpecies)
		vGenreQuick = vGenreSet.length > 0 && !(vGenreSet.length === 1 && vGenreSet[0] === inPayload.speciesIndex)
	}
	inRoot.querySelector<HTMLElement>('#sheet-filter-genre')?.setAttribute('data-visible', vGenreQuick ? 'true' : 'false')

	// Cancel any prior subscription before starting a new one — otherwise
	// a rapid tap on a different tree would leave the old listener wired.
	if (fSheetFixUnsub) { fSheetFixUnsub(); fSheetFixUnsub = null }
	fSheetFixUnsub = subscribeFix((inFix) => updateCaptureButtonState(inRoot, inFix))
	// getLatestFix() is null when no fix exists or the last one is stale
	// (> 30 s). The permanent watch does NOT spontaneously re-fire when
	// the device is stationary, so the button would otherwise hang on
	// "Active le GPS" forever (seen after a Fiche-espèce round-trip: blue
	// dot still painted, but capture disabled until a manual Location-FAB
	// tap). Actively poll once for a fresh fix — recordFix() then notifies
	// this subscriber and the button resolves on its own, exactly what the
	// FAB does manually. No-op cost (cache hit) when a fresh fix is
	// already in hand.
	if (getLatestFix() === null) void requestFreshFix().catch(() => { /* no fix yet — button stays on GPS…, the Location FAB remains the manual fallback */ })

	// Photo gallery slot — only populated when the arbre has ≥ 1 capture.
	// Re-fetches the captures on each sheet open so a stale set after a
	// previous deletion or import doesn't linger.
	void hydrateSheetGallery(inRoot, inPayload)

	inRoot.querySelector<HTMLElement>('#arbre-sheet')?.setAttribute('data-open', 'true')
}

/*
Load the captures for the currently-open arbre and (if any) mount the
photo gallery into the `#sheet-photos` slot. Hits the captures-by-arbre
IDB index + the photo blob store; the result is roughly a few hundred
bytes per row plus the photo Blobs. Object URLs are revoked between
renders so they don't outlive their thumbnails.
*/
async function hydrateSheetGallery(inRoot: HTMLElement, inPayload: ArbreTapPayload): Promise<void>
{
	const vSlot = inRoot.querySelector<HTMLElement>('#sheet-photos')
	if (!vSlot) return

	// Wipe any prior gallery + revoke its URLs before kicking off the
	// new fetch. If the user taps a different arbre fast, the late
	// load below detects the generation bump and bails.
	if (fSheetGallery) { fSheetGallery.destroy(); fSheetGallery = null }
	for (const vUrl of fSheetGalleryUrls) URL.revokeObjectURL(vUrl)
	fSheetGalleryUrls = []
	vSlot.setAttribute('data-visible', 'false')
	vSlot.innerHTML = ''

	const vGen = ++fSheetGalleryLoadGen

	let vCaptures: Capture[] = []
	try { vCaptures = await listCapturesByArbre(inPayload.id) }
	catch (vErr) { console.warn('Sheet gallery load failed', vErr); return }
	if (vGen !== fSheetGalleryLoadGen) return  // user navigated away or tapped another arbre
	if (fSheetTarget?.id !== inPayload.id) return  // sheet closed or retargeted
	if (vCaptures.length === 0) return

	// Resolve all blobs in parallel, oldest-first, then build the
	// gallery once we have a stable list. Missing blobs (post-import
	// orphans) are skipped silently.
	const vItems: PhotoGalleryItem[] = []
	for (const vCap of vCaptures)
	{
		if (typeof vCap.id !== 'number' || !vCap.photoFilename) continue
		const vBlob = await loadPhoto(vCap.photoFilename)
		if (vGen !== fSheetGalleryLoadGen) return  // bailed mid-stream
		if (!vBlob) continue
		const vUrl = URL.createObjectURL(vBlob)
		fSheetGalleryUrls.push(vUrl)
		vItems.push({
			captureId: vCap.id,
			url: vUrl,
			caption: `Capture du ${new Date(vCap.timestamp).toLocaleDateString('fr-FR')}`,
		})
	}
	if (vItems.length === 0) return
	if (vGen !== fSheetGalleryLoadGen) return
	if (fSheetTarget?.id !== inPayload.id) return

	vSlot.setAttribute('data-visible', 'true')
	const vSpeciesName = speciesNameFor(inPayload.speciesIndex)
	const vDisplayName = vSpeciesName?.commonName
		?? (vSpeciesName ? `${vSpeciesName.genre}${vSpeciesName.espece.length > 0 ? ' ' + vSpeciesName.espece : ''}` : 'cet arbre')

	fSheetGallery = mountPhotoGallery({
		host: vSlot,
		title: `Tes photos (${vItems.length})`,
		items: vItems,
		onThumbClick: (inIdx) => openGalleryLightbox(inRoot, inPayload, vItems, inIdx, vDisplayName),
		onThumbLongPress: (inIdx) => void handleDeleteFromGallery(inRoot, inPayload, vItems[inIdx]!, vDisplayName, vItems.length === 1),
	})
}

/*
Open the lightbox at `inStartIndex`. The sheet is already on the map
context, so we don't surface the "Voir sur la carte" jump button —
the user is already where the button would take them. The delete
button is wired through the same handler as the gallery's long-press.
*/
function openGalleryLightbox(
	inRoot: HTMLElement,
	inPayload: ArbreTapPayload,
	inItems: PhotoGalleryItem[],
	inStartIndex: number,
	inDisplayName: string,
): void
{
	const vLightboxPhotos: LightboxPhoto[] = inItems.map((inIt) => ({
		url: inIt.url,
		caption: inIt.caption,
		// arbreId omitted on purpose — keeps the jump-to-map button hidden
		// from the sheet flow.
		captureId: inIt.captureId,
		lat: inPayload.lat,
		lon: inPayload.lon,
	}))
	openLightbox(vLightboxPhotos, inStartIndex, {
		onDelete: async (inIdx) => {
			const vIt = inItems[inIdx]
			if (!vIt) return false
			const vIsLast = inItems.length === 1  // the lightbox's local album mirrors `inItems` length at click time
			return handleDeleteFromGallery(inRoot, inPayload, vIt, inDisplayName, vIsLast)
		},
	})
}

/*
Confirm with the user, run the IDB delete, repaint the sheet. Returns
true when the deletion landed (used by the lightbox to splice + advance).
*/
async function handleDeleteFromGallery(
	inRoot: HTMLElement,
	inPayload: ArbreTapPayload,
	inItem: PhotoGalleryItem,
	inDisplayName: string,
	inIsLast: boolean,
): Promise<boolean>
{
	const vKind = inPayload.remarquable ? 'remarquable' : 'species'
	const vConfirmed = await confirmDeleteCapture({ kind: vKind, displayName: inDisplayName, isLast: inIsLast })
	if (!vConfirmed) return false
	try
	{
		const vRes = await deleteCapture(inItem.captureId)
		// Update module-level capture-state caches BEFORE the repaint so
		// the next openSheetFor recomposition reads the new truth.
		if (vRes.wasLastForArbre) fCapturedArbres.delete(vRes.arbreId)
		if (vRes.wasLastForSpecies) fCapturedSpecies.delete(vRes.speciesIndex)
		if (vRes.wasLastForArbre || vRes.wasLastForSpecies)
		{
			// Refresh the rendered map source so pins re-lock visually.
			// Cheap on a single delete, hits MapLibre's setData path.
			// v1.5.0 : sous quick filter, re-push du subset recomputé au lieu
			// de la FC pleine (qui clobbererait le filtre sous son banner).
			if (fQuickFilter) void pushQuickSubset(inRoot)
			else void enrichAndPushDiscovery(fMountGen)
		}
		// Re-open the sheet on the same arbre — recomposes title/details/
		// buttons/photos based on the new state (which may have flipped
		// to "Arbre inconnu" or just dropped the now-deleted photo).
		if (fSheetTarget?.id === inPayload.id) openSheetFor(inRoot, inPayload)
		return true
	}
	catch (vErr)
	{
		console.warn('deleteCapture failed', vErr)
		showToast(inRoot, 'Suppression impossible', 'error', 3200)
		return false
	}
}

/*
Render the captured-arbre details block — parité ArbreDetailScreen.kt
DiscoveredContent (texte simple « Hauteur : X m · médiane Y (tag) »).
Rows with missing data are skipped (the OpenData CSV has lots of nulls
in hauteur/circonférence for very young or recently-planted trees).

L'adresse complète vient du sidecar arbre-attrs v3+ (« Adresse : … ») ;
fallback arrondissement court si elle manque.
*/
function renderDetailsHtml(inArbreId: number, inAttrs: ArbreAttrs, inSpeciesIndex: number): string
{
	const vStats = speciesInfoFor(inSpeciesIndex)?.stats ?? null  // null until ensureSpeciesInfo lands → re-render adds the médianes
	const vRows: string[] = []  // accumulated row markup, joined at end
	if (typeof inAttrs.heightM === 'number' && inAttrs.heightM > 0)
	{
		vRows.push(`<div class="sheet-detail-row"><span>Hauteur : ${inAttrs.heightM} m${medianComparison(inAttrs.heightM, vStats?.medianHm ?? null)}</span></div>`)
	}
	if (typeof inAttrs.circCm === 'number' && inAttrs.circCm > 0)
	{
		vRows.push(`<div class="sheet-detail-row"><span>Circonférence : ${inAttrs.circCm} cm${medianComparison(inAttrs.circCm, vStats?.medianCircCm ?? null)}</span></div>`)
	}
	if (inAttrs.adresse)
	{
		vRows.push(`<div class="sheet-detail-row"><span>Adresse : ${escapeHtml(inAttrs.adresse)}</span></div>`)
	}
	else if (inAttrs.arr.kind !== 'other')
	{
		vRows.push(`<div class="sheet-detail-row"><span>${escapeHtml(arrKeyLabel(inAttrs.arr))}</span></div>`)
	}
	vRows.push(`<div class="sheet-detail-row sheet-detail-id"><span>ID OpenData : ${inArbreId}</span></div>`)
	return vRows.join('')
}

/*
Update the capture button text/state based on the user's distance to
the open sheet's target arbre. Called by the fix subscription on every
geolocate event AND once immediately on subscribe (bootstrap).

Hysteresis to absorb GPS jitter:
- Below kMaxArbreMeters (30 m) → enter "ready" (latching).
- Above kMaxArbreMeters + kHysteresisMeters (35 m) → "far".
- In between (30–35 m) → keep the previous state ("ready" stays
  ready, anything else stays disabled). Without this, a 28→32→29 m
  jitter cycle would flicker the button mid-tap.

The captureFlow gate uses `kCaptureCutoffMeters` (35 m) so the button's
"ready" state is never rejected by a stricter check underneath.
*/
function updateCaptureButtonState(inRoot: HTMLElement, inFix: FreshFix | null): void
{
	const vBtn = inRoot.querySelector<HTMLButtonElement>('#sheet-capture')
	if (!vBtn || !fSheetTarget) return
	if (fCaptureInFlight) return  // don't fight the in-flight UI

	// Parité Android captureAvailability : fix absent OU plus vieux que
	// MAX_GPS_AGE_MS → NoGps. Pas de filtre d'accuracy — le seul gate de
	// précision est la distance à l'arbre ci-dessous.
	if (!inFix || Date.now() - inFix.timestamp > kMaxGpsAgeMs)
	{
		vBtn.disabled = true
		// Android CaptureButton spec : « Active le GPS » (état NoGps).
		vBtn.textContent = 'Active le GPS'
		vBtn.dataset.state = 'pending'
		return
	}

	const vDist = haversineMeters(inFix.lat, inFix.lon, fSheetTarget.lat, fSheetTarget.lon)
	const vWasReady = vBtn.dataset.state === 'ready'  // latching window: ready persists across the 5 m hysteresis band
	const vReady = vDist <= kMaxArbreMeters || (vWasReady && vDist <= kMaxArbreMeters + kHysteresisMeters)

	if (vReady)
	{
		vBtn.disabled = false
		// Android v1.0.2 spec: bouton "Recapturer" si l'arbre est déjà
		// déverrouillé. Texte unique pour les états "Capturer" et
		// "Recapturer" — la distinction signale visuellement à l'user
		// que l'arbre porte déjà au moins une de ses captures.
		const vAlreadyCaptured = fCapturedArbres.has(fSheetTarget.id)
		vBtn.textContent = vAlreadyCaptured ? 'Recapturer' : 'Capturer'
		vBtn.dataset.state = 'ready'
	}
	else
	{
		vBtn.disabled = true
		// Android v1.0.1 spec: "Trop loin (X m / max 30 m). Rapproche-toi."
		vBtn.textContent = `Trop loin (${vDist.toFixed(0)} m / max ${kMaxArbreMeters} m). Rapproche-toi.`
		vBtn.dataset.state = 'far'
	}
}

function closeSheet(inRoot: HTMLElement): void
{
	inRoot.querySelector<HTMLElement>('#arbre-sheet')?.setAttribute('data-open', 'false')
	fSheetTarget = null
	fActiveMap?.setActiveArbre(null)  // stop the pulse + clear the ring source
	if (fSheetFixUnsub) { fSheetFixUnsub(); fSheetFixUnsub = null }
	// Bump the gen so any in-flight gallery hydration bails before
	// touching the DOM. Then tear down the gallery + revoke its URLs.
	fSheetGalleryLoadGen += 1
	if (fSheetGallery) { fSheetGallery.destroy(); fSheetGallery = null }
	for (const vUrl of fSheetGalleryUrls) URL.revokeObjectURL(vUrl)
	fSheetGalleryUrls = []
}

function onSheetSpecies(): void
{
	if (!fSheetTarget) return
	navigate(kPaths.species(fSheetTarget.speciesIndex))
}

function onSheetRemarquable(): void
{
	if (!fSheetTarget || !fSheetTarget.remarquable) return
	navigate(kPaths.remarquable(fSheetTarget.id))
}

/*
v1.5.0 filtres rapides (parité Android applyQuickFilter, MapScreen.kt).
« Toute l'espèce » filtre le singleton {sk} avec le nom d'affichage de
l'espèce en label ; « Tout le genre » filtre genreFilterSet avec le nom
FR du genre (fallback latin). Dans les deux cas : sheet fermée, subset
pushé in-place sur la source persistante, caméra immobile, banner ✕.
*/
function onSheetFilterSpecies(inRoot: HTMLElement): void
{
	if (!fSheetTarget) return
	const vName = speciesNameFor(fSheetTarget.speciesIndex)
	if (!vName) return
	applyQuickFilter(inRoot, { kind: 'species', sk: fSheetTarget.speciesIndex, label: displayName(vName) })
}

function onSheetFilterGenre(inRoot: HTMLElement): void
{
	if (!fSheetTarget) return
	const vName = speciesNameFor(fSheetTarget.speciesIndex)
	if (!vName) return
	applyQuickFilter(inRoot, { kind: 'genre', genre: vName.genre, label: genreInfoFor(vName.genre)?.fr ?? vName.genre })
}

function applyQuickFilter(inRoot: HTMLElement, inQuickFilter: QuickFilter): void
{
	closeSheet(inRoot)
	// Chasse × filtre : le subset masque les remarquables non capturés, la
	// chasse deviendrait incohérente — on la ferme (le FAB ★ est de toute
	// façon masqué par .map-filtered le temps du filtre).
	if (fHuntActive) onHuntFabTap(inRoot)
	fQuickFilter = inQuickFilter
	inRoot.querySelector<HTMLElement>('.screen')?.classList.add('map-filtered')
	showQuickBanner(inRoot)
	void pushQuickSubset(inRoot)
}

/*
(Re)pousse le subset du quick filter courant sur la source persistante.
Recompute le set à chaque appel (un filtre genre s'élargit quand une
capture ajoute une espèce du genre — self-heal). setSourceData in-place,
JAMAIS setFilter (clusters fantômes), JAMAIS fitBounds ni flyTo — le
cadrage de l'utilisateur est sacré (fix v1.5.0 Android).
*/
async function pushQuickSubset(inRoot: HTMLElement): Promise<void>
{
	if (!fQuickFilter || !fActiveMap) return
	await ensureArbresFeatures()  // no-op quand déjà en mémoire (cas nominal : la carte est déjà peinte)
	if (!fQuickFilter || !fActiveMap) return  // cleared/détaché pendant l'await
	const vSet = fQuickFilter.kind === 'species'
		? new Set([fQuickFilter.sk])
		: new Set(genreFilterSet(fQuickFilter.genre, fCapturedSpecies))
	const vEffective = effectivelyCapturedSpecies(fCapturedSpecies)
	const vFc = discoveryFCForSpecies(vSet, vEffective, fCapturedArbres)
	if (!vFc) return
	fActiveMap.setSourceData(vFc.collection)
	fSourceMode = { sks: [...vSet] }
	const vCount = vFc.collection.features.length
	setText(inRoot, '#quick-filter-count', `${vCount} arbre${vCount > 1 ? 's' : ''} dans Paris`)
}

/*
✕ du banner quick filter — défiltrage in-place (parité Android
host.quickFilter = null) : pas de navigation, pas d'entrée history, pas
de mouvement caméra. La source complète enrichie est re-pushée ;
fSourceMode ne repasse à 'full' que sur push réussi (sinon le prochain
mount restaure sous son splash conditionnel).
*/
function clearQuickFilter(inRoot: HTMLElement): void
{
	fQuickFilter = null
	inRoot.querySelector<HTMLElement>('#quick-filter-banner')?.setAttribute('data-open', 'false')
	inRoot.querySelector<HTMLElement>('.screen')?.classList.remove('map-filtered')
	void enrichAndPushDiscovery(fMountGen).then((inPushed) => {
		if (inPushed)
		{
			fSourceMode = 'full'
			fDiscoveryDirty = false  // l'enrich qui vient de tourner intègre les captures du détour filtré
		}
	})
}

/*
Sheet "Capturer" handler — keep this synchronous through to the
captureFlow's first call so iOS preserves the user gesture for the
file-input click inside capturePhoto().
*/
function onSheetCapture(inRoot: HTMLElement): void
{
	if (!fSheetTarget) return
	if (fCaptureInFlight) return
	fCaptureInFlight = true

	// Haptic tap BEFORE the camera opens — Android v1.0.1 moved this
	// from post-INSERT to give the user immediate tactile confirmation
	// that the button registered.
	triggerCaptureTapHaptic()

	const vBtn = inRoot.querySelector<HTMLButtonElement>('#sheet-capture')
	if (vBtn) { vBtn.disabled = true; vBtn.textContent = 'Capture…' }

	const vTarget: TargetArbre = {
		arbreId: fSheetTarget.id,
		speciesIndex: fSheetTarget.speciesIndex,
		remarquable: fSheetTarget.remarquable,
		lat: fSheetTarget.lat,
		lon: fSheetTarget.lon,
	}
	const vPromise = runCaptureFlow(vTarget, (inStatus) => {
		try { onCaptureStatus(inRoot, inStatus) }
		catch (vErr) { console.warn('Capture status render failed', vErr) }
	})
	vPromise.finally(() => {
		fCaptureInFlight = false
		// Restore the button by re-running the distance check (it'll set
		// the right state — Capturer / Trop loin — based on current GPS).
		updateCaptureButtonState(inRoot, null)
	})
}

/*
Capture-transit splash. Mounted the instant the user validates their photo
(phase 'encoding' — fired synchronously as the camera overlay tears down) and
held opaque over the map + sheet for the whole encode/GPS/save pipeline.
Parité Android : la validation enchaîne visuellement sur la fiche espèce
(1ʳᵉ espèce) ou sur la carte + toast (capture ordinaire), jamais sur un écran
de validation figé ni un détour visible par la carte.

Mounted in document.body — NOT the screen root — so the router's innerHTML
wipe can't kill it mid-transition: on a first-of-species capture, navigation
to the species fiche happens UNDER the still-opaque splash, which then fades
out after its floor (gate resolved at the pipeline's outcome). The fiche is
already mounted when the splash lifts — no blank frame, no visible map.
*/
let fCaptureSplashGate: ((inDone: boolean) => void) | null = null

function mountCaptureSplash(): void
{
	if (document.querySelector('#splash')) return  // already up (idempotent across 'encoding'/'gps'/'saving')
	document.body.insertAdjacentHTML('beforeend', renderCaptureSplashHtml())
	const vGate = new Promise<boolean>((inResolve) => { fCaptureSplashGate = inResolve })
	void startSplash(document.body, () => vGate, { floorMs: kFilterSplashMinMs, skipTips: true })
}

function releaseCaptureSplash(): void
{
	fCaptureSplashGate?.(true)
	fCaptureSplashGate = null
}

function onCaptureStatus(inRoot: HTMLElement, inStatus: CaptureStatus): void
{
	if (inStatus.phase === 'cancelled')
	{
		// Tic + non-blocking toast — Android v1.0.1 *Vérité & Friction*:
		// "Snackbar + tic discret à l'annulation de l'app caméra".
		triggerCaptureCancelTic()
		showToast(inRoot, 'Capture annulée', 'info', 2000)
		return
	}
	if (inStatus.phase === 'encoding' || inStatus.phase === 'gps' || inStatus.phase === 'saving')
	{
		// Photo validated — cover everything until the outcome. 'gps' also
		// mounts it for the legacy <input> fallback, which can't observe the
		// validation moment ('encoding' never fires there).
		mountCaptureSplash()
		return
	}
	if (inStatus.phase === 'success')
	{
		// Visual shutter fires immediately, BEFORE we close the sheet,
		// so the user sees the wash on top of the still-open arbre
		// context — same beat the native iOS camera ships. The tap
		// haptic already fired when the Capturer button was pressed.
		triggerCaptureSuccessFlash()
		const vLabel = inStatus.speciesLabel ?? 'Arbre'
		if (inStatus.target)
		{
			fActiveMap?.setDiscovered(inStatus.target.arbreId)
			fCapturedArbres.add(inStatus.target.arbreId)
			fCapturedSpecies.add(inStatus.target.speciesIndex)
		}
		// Pas d'enrichAndPushDiscovery() ici : la reconstruction de la FC
		// 213 k + le rebuild de l'index clusters via setData, répétés à CHAQUE
		// capture, faisaient grimper la mémoire. L'arbre capturé verdit déjà via
		// setDiscovered (feature-state, ci-dessus) ; l'enrichment complet (espèce
		// entière + recolor clusters) est différé au prochain mount de la carte,
		// hors excursion caméra. On marque juste la découverte « dirty » pour que
		// ce mount-là le déclenche sous le splash conditionnel (la carte étant
		// persistante, il serait sinon skippé). Sur espèce inédite on part en
		// Fiche espèce puis on revient → l'enrich tourne une fois, là.
		fDiscoveryDirty = true
		// Si la chasse est active, recalc cible immédiat (la
		// cible peut changer de remarquable si l'utilisateur vient juste
		// d'en capturer un).
		fHuntController?.refresh()
		// v1.5.0 : sous quick filter, re-push du subset recomputé (un filtre
		// genre s'élargit si la capture révèle une nouvelle espèce du genre)
		// — in-place, caméra immobile.
		if (fQuickFilter) void pushQuickSubset(inRoot)
		if (inStatus.firstCaptureEver) void onFirstCaptureEver()
		closeSheet(inRoot)
		if (inStatus.firstForSpecies && inStatus.target)
		{
			// Première capture de l'espèce : navigation IMMÉDIATE vers la
			// fiche (parité Android ArbresNavHost.onFirstSpeciesCapture) —
			// pas de toast ni de délai, la célébration vit dans le
			// CelebrationHero de la fiche. Le wipe de navigation emporte le
			// splash de capture encore affiché : l'utilisateur passe du
			// splash à la fiche sans revoir la carte.
			releaseCaptureSplash()
			navigate(kPaths.species(inStatus.target.speciesIndex, true))
		}
		else
		{
			releaseCaptureSplash()
			showToast(inRoot, `✓ ${vLabel} capturé !`, 'success', 2400)
		}
		return
	}
	if (inStatus.phase === 'error')
	{
		releaseCaptureSplash()
		showToast(inRoot, inStatus.message ?? 'Erreur', 'error', 4200)
		return
	}
	if (inStatus.message) showToast(inRoot, inStatus.message, 'info', null)
}

/*
Fly-to a specific arbre on mount (deep link from `#/map?pulseArbreId=N`).
Resolves the arbre's lat/lon from `arbresFeatures`, then performs a
600 ms zoom-20 flight + a 2 s pulsing ring at the destination. Mirrors
Android v1.0.2 *Photos & progressivité* exactly — no sheet auto-open;
the user taps the pin themselves if they want the detail.

The `inGen` snapshot guards against the user navigating elsewhere mid-
flight (the same generation token covers MapLibre chunk loading); if
they did, we silently drop the work.
*/
async function runFlyToPulse(inGen: number, inArbreId: number): Promise<void>
{
	try
	{
		const vFeatures = await ensureArbresFeatures()
		if (inGen !== fMountGen) return
		const vF = vFeatures.find((inX) => inX.id === inArbreId)  // linear scan, one-off — 217 k entries is OK at deep-link cost
		if (!vF || !fActiveMap) return
		fActiveMap.flyTo({ lat: vF.lat, lon: vF.lon }, kPulseZoom, kPulseFlyDurationMs)
		fActiveMap.setActiveArbre({ lat: vF.lat, lon: vF.lon })
		window.setTimeout(() => {
			if (inGen !== fMountGen) return  // navigation happened during the pulse window
			fActiveMap?.setActiveArbre(null)
		}, kPulseDurationMs)
	}
	catch (vErr)
	{
		console.warn('Fly-to pulse failed', vErr)
	}
}

/*
Returns true iff the enriched FC was actually pushed to the map source —
callers must only clear `fDiscoveryDirty` / reset `fSourceMode` on true,
so a failed or superseded enrich is retried on the next mount. When
`inGen` is provided, the push is dropped if another mount started during
the awaits (e.g. the user navigated to a filtered map: pushing the full
FC would clobber the freshly-set filtered source).
*/
async function enrichAndPushDiscovery(inGen?: number): Promise<boolean>
{
	if (!fActiveMap) return false
	try
	{
		await ensureArbresFeatures()
		const [vSpecies, vArbreIds] = await Promise.all([capturedSpeciesIndices(), capturedArbreIds()])
		if (inGen !== undefined && inGen !== fMountGen) return false
		// `vArbreIds` sert de set remarquables : ids uniques, donc pour une
		// feature remarquable `id ∈ set` ⇔ ce remarquable a été capturé.
		const vFc = applyDiscoveryToFeatures(effectivelyCapturedSpecies(vSpecies), vArbreIds)
		if (!vFc) return false
		fActiveMap.setSourceData(vFc)
		return true
	}
	catch (vErr)
	{
		console.warn('Discovery enrichment failed', vErr)
		return false
	}
}

/*
First-capture side effect: ask the browser to mark our origin as
persistent storage so iOS doesn't evict the photos + cached geojson
when the device is low on space. The result is stashed in
localStorage under kPersistBannerKey — the next mount of the map
screen reads it and renders an explanatory banner.

We don't render the banner inline because the first-capture flow
immediately navigates to the species celebrate screen, taking the
user away from the map DOM. Stashing the state survives that detour.
*/
async function onFirstCaptureEver(): Promise<void>
{
	try
	{
		const vGranted = await tryPersistStorage()
		const vState: PersistBannerState = vGranted ? 'granted' : 'denied'
		window.localStorage.setItem(kPersistBannerKey, vState)
	}
	catch (vErr)
	{
		console.warn('Persistent-storage request failed', vErr)
	}
}

/*
Banner DOM. Always rendered; visibility driven by data-open. The text
slot is filled from JS based on the localStorage flag — we keep both
copies in code so the banner can adapt to "granted" vs "denied"
without juggling state across reloads.
*/
function renderPersistBanner(): string
{
	return `
		<div class="persist-banner" id="persist-banner" data-open="false" role="status">
			<span class="persist-banner-icon" aria-hidden="true">🛡️</span>
			<span class="persist-banner-text" id="persist-banner-text"></span>
			<button class="persist-banner-close" id="persist-banner-close" aria-label="Fermer">×</button>
		</div>
	`
}

/*
Filtered-map back banner (Android `FilterBanner`). Replaces the FAB stack
in filtered mode (CSS `.map-filtered` hides search / hunt / the bottom FAB
stack; the Location FAB stays). Top-left: back arrow → fiche, the
representative entry's display name, an italic latin subtitle when it
differs, a "{n} arbres dans Paris" line filled in once the filtered FC is
built, and — genre filter only — a "N / Y espèces du genre" line.
*/
function renderFilterBanner(inName: SpeciesName | null, inGenreSubtitle: string | null): string
{
	const vTitle = inName ? displayName(inName) : 'Espèce'
	const vLatin = inName ? formatLatin(inName) : ''
	const vSubtitle = vLatin && vLatin !== vTitle ? vLatin : ''
	return `
		<div class="filter-banner" id="filter-banner" role="region" aria-label="Espèce filtrée">
			<button class="filter-banner-back" id="filter-banner-back" aria-label="Retour à la fiche-espèce">←</button>
			<div class="filter-banner-text">
				<div class="filter-banner-title">${escapeHtml(vTitle)}</div>
				${vSubtitle ? `<div class="filter-banner-latin"><em>${escapeHtml(vSubtitle)}</em></div>` : ''}
				<div class="filter-banner-count" id="filter-banner-count"></div>
				${inGenreSubtitle ? `<div class="filter-banner-genre">${escapeHtml(inGenreSubtitle)}</div>` : ''}
			</div>
		</div>
	`
}

/*
v1.5.0 — banner du quick filter (Android QuickFilterBanner). Nœud dédié,
toujours rendu, visibilité par data-open : contrairement au banner route
(← history.back), son ✕ défiltre in-place. Même ancrage/style que
.filter-banner via la classe partagée.
*/
function renderQuickFilterBanner(): string
{
	return `
		<div class="filter-banner quick-filter-banner" id="quick-filter-banner" data-open="false" role="region" aria-label="Filtre rapide">
			<button class="filter-banner-clear" id="quick-filter-clear" aria-label="Retirer le filtre">×</button>
			<div class="filter-banner-text">
				<div class="filter-banner-title" id="quick-filter-title"></div>
				<div class="filter-banner-count" id="quick-filter-count"></div>
			</div>
		</div>
	`
}

function showQuickBanner(inRoot: HTMLElement): void
{
	if (!fQuickFilter) return
	setText(inRoot, '#quick-filter-title', fQuickFilter.label)
	inRoot.querySelector<HTMLElement>('#quick-filter-banner')?.setAttribute('data-open', 'true')
}

/*
"Prunier (Prunus sp.)" → "Prunier" — fallback splash label for a (G, sp.)
entry when genre-info isn't loaded (cold deep-link). Android equivalent:
`displayNomCommun.substringBefore(" (")`.
*/
function truncateBeforeParen(inText: string): string
{
	const vAt = inText.indexOf(' (')
	return vAt === -1 ? inText : inText.slice(0, vAt)
}

/*
If a pending banner state is present in localStorage, paint the
correct copy and reveal the banner. No-op when the flag is absent
(common case — banner only ever shows once, just after the first
capture, until the user dismisses it).
*/
function maybeShowPersistBanner(inRoot: HTMLElement): void
{
	const vRaw = window.localStorage.getItem(kPersistBannerKey)  // 'granted' | 'denied' | null
	if (vRaw !== 'granted' && vRaw !== 'denied') return
	const vBanner = inRoot.querySelector<HTMLElement>('#persist-banner')
	const vText = inRoot.querySelector<HTMLElement>('#persist-banner-text')
	if (!vBanner || !vText) return
	// Libellés OS-neutres : la PWA tourne aussi bien sur iOS Safari que
	// sur Android/Chromium — « le système » couvre les deux évicteurs.
	vText.textContent = vRaw === 'granted'
		? 'Tes captures et tes photos sont protégées : le système ne pourra pas les effacer pour libérer de l\'espace.'
		: 'Pour que le système ne supprime pas tes captures en cas de stockage faible, installe CanoPaname (« Ajouter à l\'écran d\'accueil » ou « Installer l\'application »).'
	vBanner.dataset.variant = vRaw
	vBanner.setAttribute('data-open', 'true')
}

function dismissPersistBanner(inRoot: HTMLElement): void
{
	inRoot.querySelector<HTMLElement>('#persist-banner')?.setAttribute('data-open', 'false')
	window.localStorage.removeItem(kPersistBannerKey)
}

/*
Toggle handler du FAB ★ Mode chasse. Mirror Android v1.2.0
MapScreen.kt:750-764 (tap ★ → bascule huntActive).

État volatile (parité Compose `remember`) : le panel se ferme au
unmount/remount de l'écran (`mountMapScreen` reset fHuntActive).

Side effects :
- Mount/destroy `HuntPanel` dans #hunt-panel-host.
- Toggle classe CSS `.map-hunt-active` sur la racine `.screen` → décale
  le fab-stack + toasts au-dessus du panel via le CSS app.css.
*/
function onHuntFabTap(inRoot: HTMLElement): void
{
	const vScreen = inRoot.querySelector<HTMLElement>('.screen')
	const vHost = inRoot.querySelector<HTMLElement>('#hunt-panel-host')
	if (!vScreen || !vHost) return

	if (fHuntActive)
	{
		// Fermeture : destroy controller, retire classe, le FAB ★
		// réapparaît via CSS.
		if (fHuntController) { fHuntController.destroy(); fHuntController = null }
		fHuntActive = false
		vScreen.classList.remove('map-hunt-active')
		return
	}

	// Ouverture : warm cache features + remarquables-info + état easter
	// egg obscure (IDB best-effort), puis mount.
	fHuntActive = true
	vScreen.classList.add('map-hunt-active')
	void (async () => {
		try
		{
			const [vFeatures, , , vObscured] = await Promise.all([
				ensureArbresFeatures(),
				ensureSpeciesIndex(),
				ensureRemarquablesInfo(),
				getRadarObscured().catch(() => false),  // best-effort, default false si IDB échoue
			])
			if (!fHuntActive) return  // user fermé pendant le load
			const vRemarquables = vFeatures.filter((inF) => inF.remarquable === true)
			fHuntController = mountHuntPanel(vHost, {
				remarquables: vRemarquables,
				getCapturedIds: () => fCapturedArbres,
				// getRawFix sans filtre — parité Android computeReadout qui
				// prend currentLocation tel quel : la chasse vise depuis la
				// dernière position connue plutôt que de décrocher en
				// « Localisation en cours… » à chaque fluctuation GPS.
				getFix: () => getRawFix(),
				resolveName: (inArbre) => {
					const vName = speciesNameFor(inArbre.speciesIndex)
					return vName ? displayName(vName) : 'Espèce indéterminée'
				},
				resolveQualification: (inArbre) => remarquableInfoFor(inArbre.id)?.qualif ?? null,
				onClose: () => onHuntFabTap(inRoot),
				initialObscured: vObscured,
				onObscureToggle: (inNext) => { void setRadarObscured(inNext).catch(() => {}) },
			})
		}
		catch (vErr)
		{
			console.warn('HuntPanel mount failed', vErr)
			fHuntActive = false
			vScreen.classList.remove('map-hunt-active')
		}
	})()
}

// FAB 🔍 Recherche universelle — overlay plein écran
// avec 3 sections (espèces capturées / genres touchés / 22 arrs). Tap
// espèce/genre → navigation fiche, tap arr → fly-to centroïde z13 700 ms
// sans remount MapScreen (parité Android v1.4.0 pendingArrFlyTo).
let fSearchController: { destroy(): void } | null = null

function onSearchFabTap(_inRoot: HTMLElement): void
{
	if (fSearchController) return  // déjà ouvert : protège contre les doubles taps
	void openSearchSheet()
}

async function openSearchSheet(): Promise<void>
{
	const [{ mountSearchSheet }] = await Promise.all([
		import('./components/SearchSheet'),
		ensureSpeciesIndex(),
		ensureGenreInfo(),
		ensureArrSpecies(),
	])
	const vCaptured = await collectCapturedSks()
	const vDismiss = (): void => {
		if (fSearchController) { fSearchController.destroy(); fSearchController = null }
	}
	fSearchController = mountSearchSheet(document.body, {
		capturedSks: vCaptured,
		onSpeciesTap: (inSk) => { vDismiss(); navigate(kPaths.species(inSk)) },
		onGenreTap: (inGenre) => { vDismiss(); navigate(kPaths.genre(inGenre)) },
		onArrTap: (inItem) => {
			vDismiss()
			fActiveMap?.flyTo({ lat: inItem.centroid[1], lon: inItem.centroid[0] }, 13, 700)
		},
		onDismiss: vDismiss,
	})
}

async function collectCapturedSks(): Promise<Set<number>>
{
	const vSet = new Set<number>()
	try
	{
		const vCaptures = await listCaptures()
		for (const vCap of vCaptures) vSet.add(vCap.speciesIndex)
	}
	catch
	{
		// soft fail — sheet ouvre avec 0 espèce capturée, l'user verra
		// quand même la section Arrondissements (aspirationnelle).
	}
	return vSet
}

/*
FAB GPS handler. Bypasses MapLibre's GeolocateControl
entirely (its programmatic trigger() failed silently on Vanadium
without a user-gesture context). Fast path = fly-to on the cached
fix that the watchPosition permanent keeps fresh; slow path = push a
fresh requestFreshFix from this tap's user-gesture context.
*/
function onGpsFabTap(inRoot: HTMLElement): void
{
	if (!fActiveMap) return

	// Visual halo on the FAB so the tap registers even if the fly-to
	// is sub-perceptible (cached fix path) or arrives a few seconds
	// later (fresh-fix path).
	const vFab = inRoot.querySelector<HTMLElement>('#fab-gps')
	if (vFab)
	{
		vFab.dataset.pulsing = 'true'
		window.setTimeout(() => { if (vFab.dataset.pulsing === 'true') vFab.dataset.pulsing = 'false' }, kGpsPulseDurationMs)
	}

	// Fast path: the watchPosition permanent left a usable fix in cache.
	// Le fly-to est visuellement explicite, pas de toast de confirmation.
	if (fActiveMap.flyToUserLocation()) return

	// Slow path: no cached fix yet. This tap IS a user gesture, so it's
	// the right moment to ask the OS for a new fix.
	showToast(inRoot, 'Localisation en cours…', 'info', null)
	requestFreshFix()
		.then(() => {
			if (!fActiveMap) return
			fActiveMap.flyToUserLocation()
			// Toast "Localisation en cours" déjà visible — on le laisse
			// se dissiper naturellement plutôt que d'empiler un succès.
			const vToast = inRoot.querySelector<HTMLElement>('#toast')
			if (vToast) vToast.setAttribute('data-open', 'false')
		})
		.catch((inErr: Error) => {
			console.warn('[mapscreen] FAB fresh fix failed', inErr)
			// Refus vs autre échec : via l'état posé sur code 1 (le message
			// OS est localisé, le substring seul ne suffit pas — gardé en
			// filet de sécurité).
			const vMessage = isGeolocationDenied() || /permission|denied/i.test(inErr.message ?? '')
				? kGeoDeniedToast
				: 'GPS indisponible — sors à découvert.'
			showToast(inRoot, vMessage, 'error', 5000)
		})
}

let fToastTimeout: number | null = null  // pending dismiss timer, null when no timer is queued

function showToast(
	inRoot: HTMLElement,
	inMessage: string,
	inVariant: 'info' | 'success' | 'error',
	inDurationMs: number | null,
): void
{
	const vToast = inRoot.querySelector<HTMLElement>('#toast')
	if (!vToast) return
	vToast.textContent = inMessage
	vToast.dataset.variant = inVariant
	vToast.setAttribute('data-open', 'true')
	if (fToastTimeout !== null) { window.clearTimeout(fToastTimeout); fToastTimeout = null }
	if (inDurationMs !== null)
	{
		fToastTimeout = window.setTimeout(() => {
			vToast.setAttribute('data-open', 'false')
			fToastTimeout = null
		}, inDurationMs)
	}
}

function setText(inRoot: HTMLElement, inSelector: string, inText: string): void
{
	const vEl = inRoot.querySelector<HTMLElement>(inSelector)
	if (vEl) vEl.textContent = inText
}

function formatLatin(inName: SpeciesName | null): string
{
	if (!inName) return 'Espèce inconnue'
	const vEspece = inName.espece.length > 0 ? ` ${inName.espece}` : ''
	return `${inName.genre}${vEspece}`
}

function escapeHtml(inText: string): string
{
	return inText
		.replace(/&/g, '&amp;')
		.replace(/</g, '&lt;')
		.replace(/>/g, '&gt;')
		.replace(/"/g, '&quot;')
		.replace(/'/g, '&#39;')
}
